import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { fetchData } from '@/services/api/data'
import type { DataResponse, DecadeData } from '@/types/data'
import type { ProgrammeItem } from '@brent/shared/types'
import { isFlagged, computeConfidenceBuckets, type ConfidenceBuckets } from '@/lib/confidence'

export const dataKeys = {
  all: ['data'] as const,
  dashboard: () => [...dataKeys.all, 'dashboard'] as const,
}

export interface DecadeWithMatched extends DecadeData {
  matched: number
}

export interface EntityDashboardStats {
  total: number
  matched: number
  flagged: number
  verified: number
  confidence: ConfidenceBuckets
}

export interface DashboardData {
  totalConcerts: number
  totalProgrammeItems: number
  fullyMatchedConcerts: number
  decades: DecadeWithMatched[]
  composers: EntityDashboardStats
  works: EntityDashboardStats
  performers: EntityDashboardStats
}

function computeEntityStats(
  items: Array<{
    confidence?: number | null
    quality_score?: string | null
    wikidata_id?: string | null
    name_canonical?: string | null
    wikidata_label?: string | null
    title_canonical?: string | null
  }>,
  nameField: 'name_canonical' | 'title_canonical',
  totalCount: number,
  matchedCount: number,
): EntityDashboardStats {
  let flaggedCount = 0
  let verifiedCount = 0
  for (const item of items) {
    const name = item[nameField] as string | null | undefined
    if (isFlagged(name, item.wikidata_label, item.confidence, item.quality_score)) {
      flaggedCount++
    }
    if (item.quality_score === 'verified') {
      verifiedCount++
    }
  }
  return {
    total: totalCount,
    matched: matchedCount,
    flagged: flaggedCount,
    verified: verifiedCount,
    confidence: computeConfidenceBuckets(items),
  }
}

function isConcertFullyMatched(
  concert: { id: number },
  piByConc: Map<number, ProgrammeItem[]>,
  verifiedComposers: Set<number>,
  verifiedWorks: Set<number>,
): boolean {
  const items = piByConc.get(concert.id) || []
  if (items.length === 0) return false
  return items.every((pi) => {
    const compOk =
      !!pi.composer_qid || !pi.composer_name || verifiedComposers.has(pi.composer_id ?? -1)
    const workOk = !!pi.work_qid || !pi.work_title || verifiedWorks.has(pi.work_id ?? -1)
    return compOk && workOk
  })
}

function computeDashboard(data: DataResponse): DashboardData {
  const { stats, by_decade, composers, works, performers, concerts, programme_items } = data

  // Build lookup maps for fully-matched calculation
  const piByConc = new Map<number, ProgrammeItem[]>()
  for (const pi of programme_items) {
    const list = piByConc.get(pi.concert_id) ?? []
    list.push(pi)
    piByConc.set(pi.concert_id, list)
  }
  const verifiedComposers = new Set(
    composers.filter((c) => c.quality_score === 'verified').map((c) => c.id),
  )
  const verifiedWorks = new Set(
    works.filter((w) => w.quality_score === 'verified').map((w) => w.id),
  )

  // Fully matched concerts
  let fullyMatchedConcerts = 0
  const matchedByDecade = new Map<number, number>()
  for (const concert of concerts) {
    if (isConcertFullyMatched(concert, piByConc, verifiedComposers, verifiedWorks)) {
      fullyMatchedConcerts++
      const decade = Math.floor(concert.year / 10) * 10
      matchedByDecade.set(decade, (matchedByDecade.get(decade) ?? 0) + 1)
    }
  }

  // Decades with matched overlay
  const decades: DecadeWithMatched[] = by_decade.map((d) => ({
    ...d,
    matched: matchedByDecade.get(d.decade) ?? 0,
  }))

  return {
    totalConcerts: stats.concerts.total,
    totalProgrammeItems: stats.programme_items.total,
    fullyMatchedConcerts,
    decades,
    composers: computeEntityStats(
      composers,
      'name_canonical',
      stats.composers.total,
      stats.composers.matched,
    ),
    works: computeEntityStats(works, 'title_canonical', stats.works.total, stats.works.matched),
    performers: computeEntityStats(
      performers,
      'name_canonical',
      stats.performers.total,
      stats.performers.matched,
    ),
  }
}

export function useDashboardData() {
  const query = useQuery<DataResponse>({
    queryKey: dataKeys.dashboard(),
    queryFn: fetchData,
  })

  const dashboard = useMemo(() => (query.data ? computeDashboard(query.data) : null), [query.data])

  return {
    ...query,
    dashboard,
  }
}
